import { useState } from 'react';
import { Alert, FileInput, Stack, Text, Textarea } from '@mantine/core';
import { AlertTriangle, Upload, Users } from 'lucide-react';
import { Badge, Button, Modal } from '../ui';
import { useBulkEnroll } from '../../hooks/useCourses';

/**
 * BulkEnrollDialog — CSV-driven enrollment. Org admins paste or upload a
 * CSV with header `email,first_name,last_name`; we validate client-side
 * (server validates again) and stream rows to the bulk_enroll Lambda.
 *
 * For larger drops (>1k rows) the API switches to a presigned-S3 upload
 * path — that's a Phase 5 follow-on. For now we ship a synchronous flow.
 */

interface BulkEnrollDialogProps {
  opened: boolean;
  onClose: () => void;
  courseId: string;
  courseTitle: string;
}

interface ParsedRow {
  email: string;
  firstName?: string;
  lastName?: string;
}

const SAMPLE_CSV = `email,first_name,last_name
alice@example.com,Alice,Jones
bob@example.com,Bob,Smith`;

export function BulkEnrollDialog({ opened, onClose, courseId, courseTitle }: BulkEnrollDialogProps) {
  const { mutateAsync, isPending, data, isError, error, reset } = useBulkEnroll();
  const [text, setText] = useState(SAMPLE_CSV);
  const [parseErr, setParseErr] = useState<string | null>(null);

  const handleClose = () => {
    reset();
    setParseErr(null);
    setText(SAMPLE_CSV);
    onClose();
  };

  const handleSubmit = async () => {
    setParseErr(null);
    const parsed = parseCsv(text);
    if (parsed.error) {
      setParseErr(parsed.error);
      return;
    }
    if (parsed.rows.length === 0) {
      setParseErr('No rows to enroll.');
      return;
    }
    await mutateAsync({ courseId, rows: parsed.rows });
  };

  const onFile = async (file: File | null) => {
    if (!file) return;
    const content = await file.text();
    setText(content);
  };

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title={
        <span>
          <Users size={14} style={{ marginRight: 6 }} /> Bulk enroll — {courseTitle}
        </span>
      }
      size="lg"
      footer={
        <>
          <Button variant="ghost" onClick={handleClose} disabled={isPending}>
            Close
          </Button>
          <Button variant="primary" onClick={handleSubmit} loading={isPending} leftIcon={<Upload size={14} />}>
            Enroll
          </Button>
        </>
      }
    >
      <Stack gap="sm">
        <Text size="sm" c="dimmed">
          Paste CSV with header <code>email,first_name,last_name</code> or upload a file. Up to 500
          rows per call. Existing enrollments are skipped silently.
        </Text>
        <FileInput
          accept=".csv,text/csv"
          leftSection={<Upload size={14} />}
          onChange={onFile}
          placeholder="Upload CSV"
        />
        <Textarea
          value={text}
          onChange={(e) => setText(e.currentTarget.value)}
          minRows={8}
          autosize
          maxRows={14}
          spellCheck={false}
          aria-label="CSV content"
          styles={{
            input: {
              fontFamily: "'JetBrains Mono', ui-monospace, monospace",
              fontSize: 12,
              backgroundColor: '#0c0c0e',
              borderColor: '#1f1f23',
            },
          }}
        />
        {parseErr ? (
          <Alert color="amber" icon={<AlertTriangle size={14} />} variant="light">
            {parseErr}
          </Alert>
        ) : null}
        {isError ? (
          <Alert color="red" icon={<AlertTriangle size={14} />} variant="light">
            {(error as Error).message}
          </Alert>
        ) : null}
        {data ? (
          <Stack gap={4}>
            <Badge variant="success">
              Enrolled {data.enrolled} · Skipped {data.skipped} · Errors {data.errors.length}
            </Badge>
            {data.errors.length > 0 ? (
              <Stack gap={2}>
                {data.errors.slice(0, 5).map((err) => (
                  <Text key={`${err.row}-${err.email}`} size="xs" c="red" ff="JetBrains Mono">
                    row {err.row} ({err.email}): {err.reason}
                  </Text>
                ))}
              </Stack>
            ) : null}
          </Stack>
        ) : null}
      </Stack>
    </Modal>
  );
}

interface ParseResult {
  rows: Array<ParsedRow>;
  error: string | null;
}

function parseCsv(input: string): ParseResult {
  const lines = input
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter((l) => l.length > 0);
  if (lines.length < 2) return { rows: [], error: 'CSV must have a header row and at least one row.' };
  const [header, ...rows] = lines;
  const cols = (header ?? '').split(',').map((c) => c.trim().toLowerCase());
  const emailIdx = cols.indexOf('email');
  if (emailIdx < 0) return { rows: [], error: 'Missing required column: email' };
  const firstIdx = cols.indexOf('first_name');
  const lastIdx = cols.indexOf('last_name');

  const parsed: Array<ParsedRow> = [];
  for (const row of rows) {
    const cells = row.split(',').map((c) => c.trim());
    const email = cells[emailIdx];
    if (!email || !email.includes('@')) {
      return { rows: [], error: `Invalid email on row ${parsed.length + 2}: ${email ?? '(empty)'}` };
    }
    const item: ParsedRow = { email };
    if (firstIdx >= 0 && cells[firstIdx]) item.firstName = cells[firstIdx];
    if (lastIdx >= 0 && cells[lastIdx]) item.lastName = cells[lastIdx];
    parsed.push(item);
  }
  return { rows: parsed, error: null };
}
